'use client';

import { useState, useMemo, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { SafeRemoteImage } from '@/components/safe-remote-image';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Plus, Minus } from 'lucide-react';
import { type ProductOptionGroup, type ProductAdditional } from '@/lib/productService';
import { additionalEffectivePrice } from '@/lib/cartPriceCalculator';
import { formatCurrency } from '@/lib/utils';

type FoodProductSelectorProps = {
  optionGroups: ProductOptionGroup[];
  additionals: ProductAdditional[];
  /** Base price used for add-to-cart (promotion price when on sale) */
  basePrice: number;
  /** Original price without discount - for strikethrough display when product has promotion */
  originalBasePrice?: number;
  locale: string;
  onSelectionChange: (selections: {
    optionGroups: Array<{ groupId: string; optionIds: string[] }>;
    additionals: Array<{ additionalId: string; quantity: number }>;
    totalPrice: number;
    /** Original total (original base + modifiers) - for strikethrough when promotion */
    originalTotal?: number;
  }) => void;
};

export function FoodProductSelector({
  optionGroups,
  additionals,
  basePrice,
  originalBasePrice,
  locale,
  onSelectionChange,
}: FoodProductSelectorProps) {
  const t = useTranslations('product');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string[]>>({});
  const [additionalQuantities, setAdditionalQuantities] = useState<Record<string, number>>({});
  const catalogAdditionals = useMemo(
    () => additionals.filter((additional) => additional.active !== false),
    [additionals]
  );

  // Calculate modifiers total (options + additionals) - same for both original and effective price
  const modifiersTotal = useMemo(() => {
    let total = 0;

    Object.entries(selectedOptions).forEach(([groupId, optionIds]) => {
      const group = optionGroups.find(g => g.id === groupId);
      if (group) {
        optionIds.forEach(optionId => {
          const option = group.options.find(o => o.id === optionId);
          if (option) {
            total += option.priceModifier;
          }
        });
      }
    });

    Object.entries(additionalQuantities).forEach(([additionalId, quantity]) => {
      const additional = catalogAdditionals.find(a => a.id === additionalId);
      if (additional && quantity > 0) {
        total += additionalEffectivePrice(additional) * quantity;
      }
    });

    return total;
  }, [selectedOptions, additionalQuantities, optionGroups, catalogAdditionals]);

  // Effective total (base price + modifiers) - used for add-to-cart
  const totalPrice = basePrice + modifiersTotal;

  // Original total (original base + modifiers) - for strikethrough when product has promotion
  const originalTotal = (originalBasePrice ?? basePrice) + modifiersTotal;

  // Update parent when selections change (use useEffect to avoid calling during render)
  useEffect(() => {
    const optionGroupSelections = Object.entries(selectedOptions).map(([groupId, optionIds]) => ({
      groupId,
      optionIds,
    }));

    const additionalSelections = Object.entries(additionalQuantities)
      .filter(([additionalId, quantity]) =>
        quantity > 0 && catalogAdditionals.some((additional) => additional.id === additionalId)
      )
      .map(([additionalId, quantity]) => ({
        additionalId,
        quantity,
      }));

    onSelectionChange({
      optionGroups: optionGroupSelections,
      additionals: additionalSelections,
      totalPrice,
      originalTotal: originalBasePrice !== undefined ? originalTotal : undefined,
    });
  }, [selectedOptions, additionalQuantities, catalogAdditionals, totalPrice, originalTotal, originalBasePrice, onSelectionChange]);

  const handleOptionToggle = (groupId: string, optionId: string) => {
    const group = optionGroups.find(g => g.id === groupId);
    if (!group) return;

    const currentSelections = selectedOptions[groupId] || [];
    const isSelected = currentSelections.includes(optionId);

    let newSelections: string[];

    if (isSelected) {
      // Remove option
      newSelections = currentSelections.filter(id => id !== optionId);
    } else {
      // Add option
      // If maxSelections is 1, replace the current selection with the new one
      if (group.maxSelections === 1) {
        newSelections = [optionId];
      } else if (group.maxSelections && currentSelections.length >= group.maxSelections) {
        // Can't add more (only applies when maxSelections > 1)
        return;
      } else {
        newSelections = [...currentSelections, optionId];
      }
    }

    // Check min/max constraints
    if (group.minSelections > 0 && newSelections.length < group.minSelections && !group.mandatory) {
      // Allow clearing if not mandatory
    }

    setSelectedOptions({
      ...selectedOptions,
      [groupId]: newSelections,
    });
  };

  const handleAdditionalQuantityChange = (additionalId: string, delta: number) => {
    const additional = catalogAdditionals.find(a => a.id === additionalId);
    if (!additional) return;

    const currentQuantity = additionalQuantities[additionalId] || 0;
    let newQuantity = currentQuantity + delta;

    // Enforce min/max constraints
    if (newQuantity < additional.minQuantity) {
      newQuantity = additional.minQuantity;
    }
    if (additional.maxQuantity !== undefined && newQuantity > additional.maxQuantity) {
      newQuantity = additional.maxQuantity;
    }

    if (newQuantity <= 0 && additional.minQuantity === 0) {
      const updated = { ...additionalQuantities };
      delete updated[additionalId];
      setAdditionalQuantities(updated);
    } else {
      setAdditionalQuantities({
        ...additionalQuantities,
        [additionalId]: newQuantity,
      });
    }
  };

  const isGroupValid = (group: ProductOptionGroup): boolean => {
    const selections = selectedOptions[group.id] || [];
    if (group.mandatory && selections.length === 0) {
      return false;
    }
    if (selections.length < group.minSelections) {
      return false;
    }
    if (group.maxSelections && selections.length > group.maxSelections) {
      return false;
    }
    return true;
  };

  const optionGroupSelectionHint = (group: ProductOptionGroup): string | null => {
    if (group.minSelections <= 0) return null;
    const min = group.minSelections;
    const max = group.maxSelections ?? null;
    if (max == null) {
      return t('food.selectCountHint.atLeast', { min });
    }
    if (min === max) {
      return min === 1
        ? t('food.selectCountHint.exactOne')
        : t('food.selectCountHint.exactOther', { count: min });
    }
    return t('food.selectCountHint.range', { min, max });
  };

  return (
    <div className="space-y-6">
      {/* Option Groups */}
      {optionGroups.map(group => {
        const catalogGroup: typeof group = {
          ...group,
          options: group.options.filter((o) => o.active !== false),
        };
        if (catalogGroup.options.length === 0) {
          return null;
        }
        const selections = selectedOptions[catalogGroup.id] || [];
        const isValid = isGroupValid(catalogGroup);
        const selectionCountHint = optionGroupSelectionHint(catalogGroup);

        return (
          <Card key={catalogGroup.id} className={!isValid ? 'border-red-500' : ''}>
            <CardHeader>
              <CardTitle className="text-lg">
                {catalogGroup.title}
                {catalogGroup.mandatory && <span className="text-red-500 ml-1">*</span>}
                {selectionCountHint != null && (
                  <span className="text-sm text-muted-foreground ml-2">
                    ({selectionCountHint})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {catalogGroup.options.map(option => {
                  const isSelected = selections.includes(option.id);
                  // For maxSelections === 1, allow switching (canSelect is always true)
                  // For maxSelections > 1, only allow if under max or already selected
                  const canSelect = catalogGroup.maxSelections === 1 || !catalogGroup.maxSelections || selections.length < catalogGroup.maxSelections || isSelected;

                  return (
                    <div
                      key={option.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'border-gray-200'
                        } ${!canSelect ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => canSelect && handleOptionToggle(catalogGroup.id, option.id)}
                    >
                      {option.imageUrl && (
                        <div className="relative w-12 h-12 flex-shrink-0 rounded-md overflow-hidden bg-muted">
                          <SafeRemoteImage
                            src={option.imageUrl}
                            alt={option.name}
                            fill
                            className="object-cover"
                            sizes="48px"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <Label className="cursor-pointer font-medium">{option.name}</Label>
                        {option.description && (
                          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                            {option.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`text-sm font-medium ${option.priceModifier > 0 ? 'text-green-600' : option.priceModifier < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                          {option.priceModifier > 0 ? `+${formatCurrency(option.priceModifier, locale)}` : option.priceModifier < 0 ? formatCurrency(option.priceModifier, locale) : t('food.included')}
                        </span>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleOptionToggle(catalogGroup.id, option.id)}
                          disabled={!canSelect}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {!isValid && (
                <p className="text-sm text-red-500 mt-2">
                  {catalogGroup.mandatory && selections.length === 0
                    ? t('food.selectionRequired')
                    : t(`food.selectAtLeast.${catalogGroup.minSelections === 1 ? 'one' : 'other'}`, { count: catalogGroup.minSelections })}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Additionals */}
      {catalogAdditionals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('food.extras')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {catalogAdditionals.map(additional => {
                const quantity = additionalQuantities[additional.id] || 0;

                return (
                  <div
                    key={additional.id}
                    className="flex items-center gap-3 p-3 border rounded-lg"
                  >
                    {additional.imageUrl && (
                      <div className="relative w-12 h-12 flex-shrink-0 rounded-md overflow-hidden bg-muted">
                        <SafeRemoteImage
                          src={additional.imageUrl}
                          alt={additional.name}
                          fill
                          className="object-cover"
                          sizes="48px"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <Label className="font-medium">{additional.name}</Label>
                      <div className="text-sm text-muted-foreground">
                        {additional.promotionPrice != null && additional.promotionPrice > 0 ? (
                          <>
                            <span className="line-through mr-1.5">
                              {formatCurrency(additional.price, locale)}
                            </span>
                            <span className="text-foreground font-medium">
                              {formatCurrency(additional.promotionPrice, locale)}
                            </span>
                          </>
                        ) : (
                          formatCurrency(additional.price, locale)
                        )}
                        {additional.allowMultiple && ` ${t('food.each')}`}
                      </div>
                    </div>
                    {additional.allowMultiple ? (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdditionalQuantityChange(additional.id, -1)}
                          disabled={quantity <= additional.minQuantity}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{quantity}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdditionalQuantityChange(additional.id, 1)}
                          disabled={additional.maxQuantity !== undefined && quantity >= additional.maxQuantity}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Checkbox
                        checked={quantity > 0}
                        onCheckedChange={(checked) =>
                          handleAdditionalQuantityChange(additional.id, checked ? 1 : -1)
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Price Summary */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Total:</span>
            <span className="text-2xl font-bold text-primary">
              {formatCurrency(totalPrice, locale)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
